import { CompleteKundali, BirthDetails, PlanetName, ZodiacSign } from '../types/kundali';
import { formatOrdinal } from './dateUtils';
import { calculateKundali } from './vedicEngine';

export type AnalysisMode = 'PARASHARI' | 'JAIMINI' | 'INTEGRATED';

export type LifeDomain =
  | 'MARRIAGE'
  | 'CAREER'
  | 'BUSINESS'
  | 'WEALTH'
  | 'PROPERTY'
  | 'CHILDREN'
  | 'EDUCATION'
  | 'FOREIGN_TRAVEL'
  | 'SPIRITUALITY'
  | 'FAME_LEADERSHIP'
  | 'HEALTH'
  | 'OTHER';

export interface EvidenceItem {
  level: 'PRIMARY' | 'SECONDARY' | 'CONFIRMATORY';
  factor: string;
  evaluation: string;
  strength: 'Strong' | 'Moderate' | 'Weak' | 'Afflicted';
}

export interface DomainFramework {
  domain: LifeDomain;
  primaryClassicalMethod: string;
  relevantVarga: string;
  karakas: string[];
  keyHouses: number[];
}

export const DOMAIN_FRAMEWORKS: { [key in LifeDomain]: DomainFramework } = {
  MARRIAGE: {
    domain: 'MARRIAGE',
    primaryClassicalMethod: '7th House, 7th Lord and Venus/Jupiter analysis',
    relevantVarga: 'D9 (Navamsha)',
    karakas: ['Venus', 'Jupiter'],
    keyHouses: [7, 2, 11]
  },
  CAREER: {
    domain: 'CAREER',
    primaryClassicalMethod: '10th House, 10th Lord and Lagna/Lagna Lord analysis',
    relevantVarga: 'D10 (Dashamsha)',
    karakas: ['Sun', 'Saturn', 'Mercury'],
    keyHouses: [10, 1, 6, 11]
  },
  BUSINESS: {
    domain: 'BUSINESS',
    primaryClassicalMethod: '7th, 10th, and 11th House Lord connectivity',
    relevantVarga: 'D10 / D11 / D2',
    karakas: ['Mercury', 'Jupiter'],
    keyHouses: [7, 10, 11, 2, 1]
  },
  WEALTH: {
    domain: 'WEALTH',
    primaryClassicalMethod: 'Dhana Yogas, 2nd & 11th House Lord analysis',
    relevantVarga: 'D2 (Hora) & D11 (Rudramsha)',
    karakas: ['Jupiter', 'Venus'],
    keyHouses: [2, 11, 5, 9, 1]
  },
  PROPERTY: {
    domain: 'PROPERTY',
    primaryClassicalMethod: '4th House, 4th Lord, and Mars dignity',
    relevantVarga: 'D4 (Chaturthamsha)',
    karakas: ['Mars', 'Saturn'],
    keyHouses: [4, 11]
  },
  CHILDREN: {
    domain: 'CHILDREN',
    primaryClassicalMethod: '5th House, 5th Lord, and Jupiter analysis',
    relevantVarga: 'D7 (Saptamsha)',
    karakas: ['Jupiter'],
    keyHouses: [5, 9, 11]
  },
  EDUCATION: {
    domain: 'EDUCATION',
    primaryClassicalMethod: '4th & 5th House, Mercury and Jupiter analysis',
    relevantVarga: 'D24 (Siddhamsha)',
    karakas: ['Mercury', 'Jupiter'],
    keyHouses: [4, 5, 9]
  },
  FOREIGN_TRAVEL: {
    domain: 'FOREIGN_TRAVEL',
    primaryClassicalMethod: '3rd, 9th, and 12th House with Rahu connectivity',
    relevantVarga: 'D4 / D9',
    karakas: ['Rahu', 'Moon'],
    keyHouses: [3, 7, 9, 12]
  },
  SPIRITUALITY: {
    domain: 'SPIRITUALITY',
    primaryClassicalMethod: '9th & 12th House, Jupiter and Ketu dignity',
    relevantVarga: 'D20 (Vimshamsha)',
    karakas: ['Jupiter', 'Ketu'],
    keyHouses: [5, 9, 12, 8]
  },
  FAME_LEADERSHIP: {
    domain: 'FAME_LEADERSHIP',
    primaryClassicalMethod: 'Raja Yogas, 5th, 9th, and 10th House Sun analysis',
    relevantVarga: 'D10 (Dashamsha)',
    karakas: ['Sun', 'Moon'],
    keyHouses: [1, 5, 9, 10, 11]
  },
  HEALTH: {
    domain: 'HEALTH',
    primaryClassicalMethod: '1st, 6th, and 8th Houses and Lagna Lord analysis',
    relevantVarga: 'D6 (Shashtamsha)',
    karakas: ['Saturn', 'Sun', 'Mars'],
    keyHouses: [1, 6, 8, 12]
  },
  OTHER: {
    domain: 'OTHER',
    primaryClassicalMethod: 'General Lagna, Lagna Lord and functional planetary placements',
    relevantVarga: 'D1 (Rashi) / D9 (Navamsha)',
    karakas: ['Sun', 'Jupiter'],
    keyHouses: [1, 5, 9]
  }
};

export interface IntervalAscendantSensitivity {
  isLagnaBoundarySensitive: boolean;
  nominalSign: string;
  nominalDegree: number;
  startSign: string;
  startDegree: number;
  endSign: string;
  endDegree: number;
  uncertaintyMinutes: number;
  crossesSignBoundary: boolean;
}

/**
 * Deterministic interval-based Ascendant sensitivity evaluation.
 * Evaluates the exact Ascendant at (nominal - uncertaintyMinutes) and (nominal + uncertaintyMinutes).
 * Replaces mean degree-approximation with exact ephemeris calculations at window boundaries.
 */
export function checkIntervalAscendantSensitivity(
  birthDetails: Partial<BirthDetails> & { date: string; time: string; latitude: number; longitude: number; timezoneOffsetMinutes?: number },
  uncertaintyMinutes: number = 5
): IntervalAscendantSensitivity {
  const completeDetails: BirthDetails = {
    name: birthDetails.name || 'Anonymous',
    gender: birthDetails.gender || 'male',
    placeName: birthDetails.placeName || 'Unknown',
    date: birthDetails.date,
    time: birthDetails.time,
    latitude: birthDetails.latitude,
    longitude: birthDetails.longitude,
    timezoneOffsetMinutes: birthDetails.timezoneOffsetMinutes ?? 330,
    ayanamsha: birthDetails.ayanamsha
  };

  const [h, m, s] = (completeDetails.time || '12:00:00').split(':').map(Number);
  const nominalSec = (h || 0) * 3600 + (m || 0) * 60 + (s || 0);
  const deltaSec = Math.max(1, uncertaintyMinutes) * 60;

  const startSec = Math.max(0, nominalSec - deltaSec);
  const endSec = Math.min(86399, nominalSec + deltaSec);

  const formatSec = (total: number) => {
    const hh = String(Math.floor(total / 3600)).padStart(2, '0');
    const mm = String(Math.floor((total % 3600) / 60)).padStart(2, '0');
    const ss = String(total % 60).padStart(2, '0');
    return `${hh}:${mm}:${ss}`;
  };

  const startTime = formatSec(startSec);
  const endTime = formatSec(endSec);

  const kStart = calculateKundali({ ...completeDetails, time: startTime });
  const kNominal = calculateKundali(completeDetails);
  const kEnd = calculateKundali({ ...completeDetails, time: endTime });

  const ascStart = kStart.planets.find(p => p.name === 'Ascendant') || kStart.planets[0];
  const ascNominal = kNominal.planets.find(p => p.name === 'Ascendant') || kNominal.planets[0];
  const ascEnd = kEnd.planets.find(p => p.name === 'Ascendant') || kEnd.planets[0];

  const crossesSignBoundary = (ascStart.signIndex !== ascNominal.signIndex) || (ascNominal.signIndex !== ascEnd.signIndex);

  return {
    isLagnaBoundarySensitive: crossesSignBoundary,
    nominalSign: ascNominal.sign,
    nominalDegree: ascNominal.degreeInSign,
    startSign: ascStart.sign,
    startDegree: ascStart.degreeInSign,
    endSign: ascEnd.sign,
    endDegree: ascEnd.degreeInSign,
    uncertaintyMinutes,
    crossesSignBoundary
  };
}

export interface JyotishaAnalysisResult {
  domain: LifeDomain;
  mode: AnalysisMode;
  question: string;
  natalPromiseScore: number | null; // Null in Stage 2/Stage 7; uncalibrated scores deferred until formal methodology certification
  timingScore: number | null;       // Null in Stage 2/Stage 7
  confidenceLevel: 'VERY HIGH' | 'HIGH' | 'MODERATE' | 'LOW' | 'INCONCLUSIVE';
  compositeDomainVerdict?: 'INCONCLUSIVE' | string;
  evidencePlan: {
    primary: string[];
    secondary: string[];
    timing: string[];
    confirmation: string[];
  };
  evidenceList: EvidenceItem[];
  directAnswer: string;
  whyPoints: string[];
  conflictingEvidence: string[];
  isConfigurationSensitive: boolean;
  sensitivityDetails?: IntervalAscendantSensitivity;
  detailedAnalysis: {
    d1Analysis: string;
    vargaAnalysis: string;
    strengthAnalysis: string;
    dashaTimingAnalysis: string;
    transitConfirmation: string;
    jaiminiVerdict?: string;
    synthesis: string;
  };
  aiCommentaryAvailable?: boolean;
  aiContradictionDetected?: boolean;
  contradictionDetails?: string[];
  calculatedAt?: string;
}

/**
 * Generate authentic, chart-derived classical foundation from real Kundali data.
 * Strictly eliminates synthetic string-length formulas, random seeds, and fabricated weights.
 */
export function prepareClassicalFoundation(
  kundali: CompleteKundali,
  domain: LifeDomain,
  mode: AnalysisMode,
  question: string,
  birthTimeUncertaintyMinutes: number = 5
): JyotishaAnalysisResult {
  const fw = DOMAIN_FRAMEWORKS[domain] || DOMAIN_FRAMEWORKS.OTHER;
  
  // 1. Authoritative Ascendant & Placements
  const lagnaPos = kundali.planets?.find(p => p.name === 'Ascendant') || {
    name: 'Ascendant' as PlanetName,
    sign: kundali.houses?.[0]?.sign || (kundali as any).ascendant?.sign || 'Aries',
    signIndex: kundali.houses?.[0]?.signIndex ?? 0,
    degreeInSign: (kundali as any).ascendant?.degree ?? 15.0,
    degreeFormatted: `${(kundali as any).ascendant?.degree ?? 15}° 00' 00"`,
    signLord: kundali.houses?.[0]?.signLord || 'Mars',
    house: 1
  };
  const lagnaLordPlanet = kundali.planets?.find(p => p.name === lagnaPos.signLord);

  // 2. Primary Domain House Analysis
  const primaryHouseNum = fw.keyHouses[0];
  const domainHouse = kundali.houses?.find(h => h.houseNumber === primaryHouseNum) || kundali.houses?.[0] || {
    houseNumber: primaryHouseNum,
    sign: lagnaPos.sign,
    signIndex: lagnaPos.signIndex,
    signLord: lagnaPos.signLord,
    degreeStart: 0,
    planetsInHouse: []
  };
  const domainLordPlanet = kundali.planets?.find(p => p.name === domainHouse.signLord);
  const naturalKaraka = kundali.planets?.find(p => p.name === fw.karakas[0]);
  const occupants = domainHouse.planetsInHouse || [];

  // 3. Active Vimshottari Dasha
  const activeDasha = kundali.dashas?.find(d => d.isCurrent) || kundali.dashas?.[0];
  const activeAntardasha = activeDasha?.antardashas?.find(ad => ad.isCurrent);

  // 4. Interval-based Ascendant Sensitivity
  let sensitivity: IntervalAscendantSensitivity | undefined;
  let isBoundarySensitive = false;
  if (kundali.birthDetails && typeof kundali.birthDetails.date === 'string' && typeof kundali.birthDetails.time === 'string') {
    try {
      sensitivity = checkIntervalAscendantSensitivity(kundali.birthDetails, birthTimeUncertaintyMinutes);
      isBoundarySensitive = sensitivity.isLagnaBoundarySensitive;
    } catch {
      // Fallback to Sandhi boundary check if interval calculation fails
      const deg = lagnaPos.degreeInSign;
      isBoundarySensitive = deg <= 1.0 || deg >= 29.0;
    }
  } else {
    const deg = lagnaPos.degreeInSign;
    isBoundarySensitive = deg <= 1.0 || deg >= 29.0;
  }

  // 5. Authentic Evidence List
  const evidenceList: EvidenceItem[] = [
    {
      level: 'PRIMARY',
      factor: `D1 ${formatOrdinal(primaryHouseNum)} House (${domainHouse.sign}) Lord ${domainHouse.signLord}`,
      evaluation: domainLordPlanet
        ? `Lord ${domainLordPlanet.name} is situated in House ${domainLordPlanet.house} (${domainLordPlanet.sign}), dignity: ${domainLordPlanet.dignity}.`
        : `House ${primaryHouseNum} lord placement recorded.`,
      strength: domainLordPlanet?.dignity === 'Exalted' || domainLordPlanet?.dignity === 'Own House'
        ? 'Strong'
        : domainLordPlanet?.dignity === 'Debilitated' || domainLordPlanet?.dignity === 'Enemy'
          ? 'Weak'
          : 'Moderate'
    },
    {
      level: 'PRIMARY',
      factor: `Ascendant (Lagna) Foundation: ${lagnaPos.sign} ${lagnaPos.degreeFormatted || ''}`,
      evaluation: lagnaLordPlanet
        ? `Lagna Lord ${lagnaLordPlanet.name} resides in House ${lagnaLordPlanet.house} (${lagnaLordPlanet.sign}), dignity: ${lagnaLordPlanet.dignity}.`
        : `Lagna sign ${lagnaPos.sign} established.`,
      strength: lagnaLordPlanet?.dignity === 'Exalted' || lagnaLordPlanet?.dignity === 'Own House'
        ? 'Strong'
        : 'Moderate'
    },
    {
      level: 'PRIMARY',
      factor: `Primary Karaka (${fw.karakas[0]}) Placement`,
      evaluation: naturalKaraka
        ? `Natural significator ${naturalKaraka.name} is in House ${naturalKaraka.house} (${naturalKaraka.sign}), dignity: ${naturalKaraka.dignity}.`
        : `Significator ${fw.karakas[0]} identified.`,
      strength: naturalKaraka?.dignity === 'Exalted' || naturalKaraka?.dignity === 'Own House' ? 'Strong' : 'Moderate'
    },
    {
      level: 'SECONDARY',
      factor: `Active Vimshottari Dasha Cycle`,
      evaluation: activeDasha
        ? `Operational Mahadasha: ${activeDasha.planet} (${activeDasha.startDate} to ${activeDasha.endDate})${activeAntardasha ? `, Antardasha: ${activeAntardasha.planet}` : ''}.`
        : `Dasha timeline evaluated.`,
      strength: 'Moderate'
    }
  ];

  if (occupants.length > 0) {
    evidenceList.push({
      level: 'CONFIRMATORY',
      factor: `${formatOrdinal(primaryHouseNum)} House Occupants`,
      evaluation: `House ${primaryHouseNum} is occupied by: ${occupants.join(', ')}.`,
      strength: 'Moderate'
    });
  }

  const evidencePlan = {
    primary: [`${formatOrdinal(primaryHouseNum)} House Lord (${domainHouse.signLord})`, `Lagna Lord (${lagnaPos.signLord})`, fw.karakas[0]],
    secondary: [fw.relevantVarga, 'Vimshottari Dasha', 'Planetary Dignities'],
    timing: ['Vimshottari Mahadasha / Antardasha', 'Gochar Transits'],
    confirmation: mode === 'JAIMINI' ? ['Chara Dasha', 'Amatyakaraka / Darakaraka'] : ['Ashtakavarga', 'Bhava Balas']
  };

  const directAnswer = `Classical Jyotish Foundation: Ascendant is ${lagnaPos.sign} (Lord: ${lagnaPos.signLord}${lagnaLordPlanet ? ` in House ${lagnaLordPlanet.house}` : ''}). The domain-significator ${formatOrdinal(primaryHouseNum)} house is ${domainHouse.sign} (Lord: ${domainHouse.signLord}${domainLordPlanet ? ` in House ${domainLordPlanet.house}, dignity ${domainLordPlanet.dignity}` : ''}). Active Mahadasha is ${activeDasha?.planet || 'unspecified'}. Composite domain verdict is maintained as INCONCLUSIVE pending validated multi-factor synthesis.`;

  const whyPoints = [
    `Ascendant is ${lagnaPos.sign} (${lagnaPos.degreeFormatted || ''}), with Lagna Lord ${lagnaPos.signLord}${lagnaLordPlanet ? ` placed in House ${lagnaLordPlanet.house}` : ''}.`,
    `Domain ${formatOrdinal(primaryHouseNum)} House (${domainHouse.sign}) Lord ${domainHouse.signLord}${domainLordPlanet ? ` occupies House ${domainLordPlanet.house} in ${domainLordPlanet.dignity} status` : ''}.`,
    `Current timing is governed by ${activeDasha?.planet || 'unknown'} Mahadasha${activeAntardasha ? ` / ${activeAntardasha.planet} Antardasha` : ''}.`
  ];

  const conflictingEvidence: string[] = [];
  if (isBoundarySensitive) {
    conflictingEvidence.push(`Birth time is sensitive to a sign boundary within ±${birthTimeUncertaintyMinutes} minutes. Divisional positions and house cusps may alter with slight clock shifts.`);
  }
  if (domainLordPlanet && (domainLordPlanet.dignity === 'Debilitated' || domainLordPlanet.dignity === 'Enemy' || domainLordPlanet.dignity === 'Great Enemy')) {
    conflictingEvidence.push(`Domain house lord ${domainLordPlanet.name} holds ${domainLordPlanet.dignity} dignity in natal D1.`);
  }

  return {
    domain,
    mode,
    question,
    natalPromiseScore: null,
    timingScore: null,
    confidenceLevel: 'INCONCLUSIVE',
    compositeDomainVerdict: 'INCONCLUSIVE',
    evidencePlan,
    evidenceList,
    directAnswer,
    whyPoints,
    conflictingEvidence,
    isConfigurationSensitive: isBoundarySensitive,
    sensitivityDetails: sensitivity,
    detailedAnalysis: {
      d1Analysis: `D1 Natal Placement: Ascendant ${lagnaPos.sign} (${lagnaPos.degreeFormatted || ''}). The ${formatOrdinal(primaryHouseNum)} house (${domainHouse.sign}) lord ${domainHouse.signLord} is situated in House ${domainLordPlanet?.house ?? 'N/A'}${domainLordPlanet ? ` in ${domainLordPlanet.sign} (${domainLordPlanet.dignity})` : ''}.${occupants.length > 0 ? ` Occupants: ${occupants.join(', ')}.` : ' No planets occupy this house directly.'}`,
      vargaAnalysis: `Divisional analysis targets ${fw.relevantVarga} for refinement of the natal promise.`,
      strengthAnalysis: `Dignity of Lagna Lord (${lagnaPos.signLord}): ${lagnaLordPlanet?.dignity || 'Neutral'}. Dignity of ${formatOrdinal(primaryHouseNum)} Lord (${domainHouse.signLord}): ${domainLordPlanet?.dignity || 'Neutral'}. Natural Karaka (${fw.karakas[0]}): ${naturalKaraka?.dignity || 'Neutral'}.`,
      dashaTimingAnalysis: activeDasha ? `Active Vimshottari Mahadasha: ${activeDasha.planet} (${activeDasha.startDate} to ${activeDasha.endDate})${activeAntardasha ? `, Antardasha: ${activeAntardasha.planet}` : ''}.` : 'Dasha cycle active.',
      transitConfirmation: `Transits to be confirmed relative to natal Moon in ${kundali.planets.find(p => p.name === 'Moon')?.sign || 'natal sign'}.`,
      jaiminiVerdict: mode !== 'PARASHARI' ? `Jaimini Karakas to be verified against Arudha Lagna.` : undefined,
      synthesis: `Deterministic astronomical placements verified. Qualitative commentary provides advisory perspective; mathematical composite scores remain deferred.`
    },
    calculatedAt: new Date().toISOString()
  };
}
